import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router-dom';

import { useCatch } from '~/libs/hooks/useCatch';
import { logout } from '~/modules/auth/libs/apis';

import { ErrorDisplay } from './ErrorDisplay';

/**
 * _app.tsx의 Catch 훅에서 사용하는 에러 컴포넌트입니다.
 * AuthProvider보다 바깥 쪽에 있기 때문에 로그아웃 처리를 직접합니다.
 */
export const AppErrorBoundary = () => {
  const error = useRouteError();
  const { t } = useTranslation('error_page');
  const { catchError } = useCatch();
  const QueryClient = useQueryClient();

  const [errorMessage, errorType] = catchError(error, {
    silence: true,
  });

  console.error('[AppErrorBoundary] ', error);

  const errorIcon = useMemo(() => {
    switch (errorType) {
      case 'page-network-error':
        return;
    }
    return undefined;
  }, [errorMessage, errorType]);

  const formattedErrorMessage = useMemo(() => {
    if (errorType === 'unknown') return t('에러가 발생했습니다.');
    if (errorType === 'page-network-error') return t('네트워크 연결이 끊겼습니다.');
    return errorMessage;
  }, [errorMessage, errorType]);

  const formattedErrorDescription = useMemo(() => {
    if (errorType === 'unknown') {
      return '아직 원인을 파악할 수 없는 오류가 발생했습니다. 아래의 버튼을 눌러 다른 방법을 시도해보시거나, 카스웍스 고객센터를 통해 오류를 신고해주세요.';
    }
    if (errorType === 'page-network-error') {
      return '네트워크 연결이 끊겨 페이지를 불러오지 못했습니다. 네트워크 연결을 확인 후 다시 시도해주세요.';
    }

    return errorMessage;
  }, [errorMessage, errorType]);

  const handleReset = async () => {
    window.location.href = '/';
  };

  const handleForceLogout = async () => {
    localStorage.removeItem('JWT_TOKEN');
    try {
      await logout();
    } catch (error) {
      console.log('로그아웃 API 호출 중 오류가 발생했습니다.', error);
    }
    QueryClient.clear();
    location.href = '/';
  };

  const buttonOptions = () => {
    if (errorType === 'page-network-error') {
      return {
        resetButtonText: '새로고침',
        onReset: () => {
          location.reload();
        },
      };
    }
  };

  return (
    <>
      <ErrorDisplay
        icon={errorIcon}
        title={formattedErrorMessage}
        description={formattedErrorDescription}
        resetButtonText={t('홈으로 이동')}
        cancelButtonText={t('error_page:m_log_out')}
        onReset={handleReset}
        onCancel={handleForceLogout}
        {...buttonOptions()}
      />
    </>
  );
};
